import { useState, useCallback, useEffect, useMemo } from "react"
import { lumi } from "../lib/lumi"
import type { IssueStatus } from "./useEquipmentIssues"

// 장비별 "확인 시각" 저장 키 (사용자 브라우저 단위)
const SEEN_KEY = "pavetech:equipmentIssueSeen"
// 며칠 이내의 변경까지 <신규/변경>으로 강조할지
const NEW_WINDOW_DAYS = 7

export interface EquipmentIssueStat {
  issued: number
  checking: number
  resolved: number
  unavailable: number
  total: number
  /** 해당 장비 이슈들의 가장 최근 등록/변경 시각 */
  latestActivityAt: string
  /** 마지막 확인 이후 새로 등록되거나 상태가 바뀐 이슈가 있는지 */
  hasUpdate: boolean
}

export type EquipmentIssueStatMap = Record<string, EquipmentIssueStat>

interface StatRow {
  equipmentId: string
  status: IssueStatus
  at: string
}

function readSeenMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {}
  } catch {
    return {}
  }
}

function writeSeenMap(map: Record<string, string>) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(map))
  } catch (error) {
    console.error("장비 이슈 확인 시각 저장 실패:", error)
  }
}

/**
 * 전체 장비의 이슈 상태별 집계를 조회한다.
 * - 장비 목록 화면에서 이슈등록 / 확인중 / 조치완료 개수를 실시간 표시하기 위한 용도
 * - 폴링 + 창 포커스 시 자동 갱신
 */
export function useEquipmentIssueStats(pollMs = 60000) {
  const [rows, setRows] = useState<StatRow[]>([])
  const [loading, setLoading] = useState(false)
  const [seen, setSeen] = useState<Record<string, string>>(() => readSeenMap())

  const fetchStats = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.equipment_issues.list({
        projection: { equipmentId: 1, status: 1, updatedAt: 1, createdAt: 1 },
        sort: { updatedAt: -1 },
        limit: 2000,
      })
      const next: StatRow[] = (list || []).map((it: any) => ({
        equipmentId: String(it?.equipmentId || ""),
        status: (it?.status || "issued") as IssueStatus,
        at: String(it?.updatedAt || it?.createdAt || ""),
      }))
      setRows(next)
      console.log("equipment issue stats fetched:", next.length)
    } catch (error) {
      console.error("장비 이슈 현황 조회 실패:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  // 최초 1회 + 주기적 폴링 (실시간 반영)
  useEffect(() => {
    fetchStats()
    if (!pollMs) return
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") fetchStats()
    }, pollMs)
    return () => window.clearInterval(timer)
  }, [fetchStats, pollMs])

  // 다른 탭/창에서 돌아왔을 때 즉시 갱신
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchStats()
    }
    window.addEventListener("focus", onVisible)
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      window.removeEventListener("focus", onVisible)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [fetchStats])

  const statMap = useMemo<EquipmentIssueStatMap>(() => {
    const cutoff = Date.now() - NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000
    const map: EquipmentIssueStatMap = {}

    rows.forEach((row) => {
      if (!row.equipmentId) return
      const cur =
        map[row.equipmentId] ||
        (map[row.equipmentId] = {
          issued: 0,
          checking: 0,
          resolved: 0,
          unavailable: 0,
          total: 0,
          latestActivityAt: "",
          hasUpdate: false,
        })

      if (row.status === "issued") cur.issued += 1
      else if (row.status === "checking") cur.checking += 1
      else if (row.status === "resolved") cur.resolved += 1
      else if (row.status === "unavailable") cur.unavailable += 1
      cur.total += 1

      if (row.at && row.at > cur.latestActivityAt) cur.latestActivityAt = row.at
    })

    Object.keys(map).forEach((equipmentId) => {
      const stat = map[equipmentId]
      const latestMs = stat.latestActivityAt ? new Date(stat.latestActivityAt).getTime() : 0
      if (!latestMs || latestMs < cutoff) {
        stat.hasUpdate = false
        return
      }
      const seenAt = seen[equipmentId]
      stat.hasUpdate = !seenAt || stat.latestActivityAt > seenAt
    })

    return map
  }, [rows, seen])

  /** 장비 이슈 화면을 확인한 시점을 기록 → 이후 새 변경만 강조 */
  const markSeen = useCallback((equipmentId: string) => {
    if (!equipmentId) return
    setSeen((prev) => {
      const next = { ...prev, [equipmentId]: new Date().toISOString() }
      writeSeenMap(next)
      return next
    })
  }, [])

  return { statMap, loading, fetchStats, markSeen }
}
